/* import __COLOCATED_TEMPLATE__ from './inspect-sections.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent-2 */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type Store from '@ember-data/store';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import {
  type PlaygroundQuestionSource,
  type AppliedGuideline,
} from 'embercom/lib/fin-playground/question';
import { objectIcons } from 'embercom/models/data/matching-system/matching-constants';
import type Session from 'embercom/services/session';
import { KNOWLEDGE_HUB_ENTITIES_NAMES } from 'embercom/lib/knowledge-hub/constants';
import { trackedReset } from 'tracked-toolbox';
import { EntityType } from 'embercom/models/data/entity-types';
import type { GuidelineCategory } from 'embercom/models/ai-agent/guidelines';

interface InspectSectionsComponentArgs {
  aiToneOfVoice?: string;
  aiAnswerLength?: string;
  appliedGuidelines?: AppliedGuideline[];
  sources?: PlaygroundQuestionSource[];
  fallbackSearchLocale?: string;
  realtimeTranslationApplied?: boolean;
  questionId?: number;
}

type InspectSectionsComponentSignature = {
  Args: InspectSectionsComponentArgs;
  Element: HTMLDivElement;
};

export interface InspectItem {
  id: string;
  icon: InterfaceIconName;
  title: string;
  isDeleted?: boolean;
  iconTooltipText: string;
  link?: {
    route: string;
    query?: $TSFixMe;
    models?: $TSFixMe;
  };
}

export default class InspectSections extends Component<InspectSectionsComponentSignature> {
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare session: Session;
  @service declare appService: AppService;
  @service declare intercomEventService: $TSFixMe;

  @trackedReset('args.questionId') openGuidanceSectionId = '';
  @trackedReset('args.questionId') openContentSectionId = '';
  @trackedReset('args.questionId') openOtherSectionId = '';
  @trackedReset('args.questionId') viewingAllContentSources = false;
  @trackedReset('args.questionId') viewingAllGuidanceSources = false;
  @trackedReset('args.questionId') viewingAllOtherSettings = false;

  entityName(entityType: keyof typeof KNOWLEDGE_HUB_ENTITIES_NAMES) {
    return KNOWLEDGE_HUB_ENTITIES_NAMES[entityType];
  }

  transformEntityName(name: string): string {
    return name.replace(/_/g, '-');
  }

  guidanceIcon(type?: GuidelineCategory): InterfaceIconName {
    switch (type) {
      case 'tone':
        return 'messenger';
      case 'clarification':
        return 'messenger-questions-filled';
      case 'routing':
        return 'new-direct-message';
      case 'content':
        return 'article';
      default:
        return 'list';
    }
  }

  get isStandaloneApp(): boolean {
    return this.appService.app.canUseStandalone;
  }

  get toneOfVoiceIcon(): InterfaceIconName {
    switch (this.args.aiToneOfVoice) {
      case 'friendly':
        return 'wave';
      case 'neutral':
        return 'fin';
      case 'plainspoken':
        return 'book';
      case 'professional':
        return 'newspaper';
      case 'playful':
        return 'lwr-happy';
      default:
        return 'lwr-happy';
    }
  }

  get answerLengthIcon() {
    switch (this.args.aiAnswerLength) {
      case 'succinct':
        return 'short-text';
      case 'standard':
        return 'long-text';
      case 'comprehensive':
        return 'extra-long-text';
      default:
        return 'extra-long-text';
    }
  }

  get realTimeTranslationIcon(): InterfaceIconName {
    return 'language';
  }

  get workflowConnectorActionIcon(): InterfaceIconName {
    return 'webhook';
  }

  get sourcesWithoutWorkflowConnectorActions() {
    return this.args.sources?.filter(
      (source) => source.entityType !== EntityType.WorkflowConnectorAction,
    );
  }

  get contentSources(): InspectItem[] {
    return (this.sourcesWithoutWorkflowConnectorActions || []).map((source) => ({
      id: source.entityId.toString(),
      icon: objectIcons[source.entityType],
      title: source.title,
      isDeleted: source.deleted,
      iconTooltipText: this.intl.t(
        `ai-agent.playground.inspect-sources.tooltips.source-entity-types.${this.transformEntityName(
          this.entityName(source.entityType),
        )}`,
      ),
      link: {
        route: 'apps.app.knowledge-hub.view',
        models: [this.session.workspace, this.entityName(source.entityType), source.entityId],
      },
    }));
  }

  get personalitySettings(): InspectItem[] {
    let settings: InspectItem[] = [];

    if (this.args.aiToneOfVoice) {
      settings.push({
        id: 'tone-of-voice',
        icon: this.toneOfVoiceIcon,
        title: this.intl.t(`ai-agent.playground.tone-of-voice.${this.args.aiToneOfVoice}`),
        iconTooltipText: this.intl.t('ai-agent.playground.inspect-sources.tooltips.basic-guidance'),
      });
    }

    if (this.args.aiAnswerLength) {
      settings.push({
        id: 'answer-length',
        icon: this.answerLengthIcon,
        title: this.intl.t(`ai-agent.playground.answer-length.${this.args.aiAnswerLength}`),
        iconTooltipText: this.intl.t('ai-agent.playground.inspect-sources.tooltips.basic-guidance'),
      });
    }

    return settings;
  }

  get allGuidanceSources() {
    let personalityItems = this.personalitySettings.map((setting) => ({
      ...setting,
      link: {
        route: 'apps.app.automation.fin-ai-agent.guidance',
        query: { openSectionId: 'tone-of-voice-and-answer-settings' },
      },
    }));

    let guidelines = (this.args.appliedGuidelines || []).map((guideline) => ({
      id: guideline.id,
      icon: this.guidanceIcon(guideline.category),
      title: guideline.title,
      iconTooltipText: this.intl.t(
        `ai-agent.playground.inspect-sources.tooltips.guidance-categories.${guideline.category}`,
      ),
      link: {
        route: this.isStandaloneApp
          ? 'apps.app.standalone.guidance'
          : 'apps.app.automation.fin-ai-agent.guidance',
      },
    }));

    return [...personalityItems, ...guidelines];
  }

  get otherSettings() {
    let settings: InspectItem[] = [];

    let workflowConnectorActions =
      this.args.sources
        ?.filter((source) => source.entityType === EntityType.WorkflowConnectorAction)
        .map((source) => ({
          id: source.entityId.toString(),
          icon: this.workflowConnectorActionIcon,
          title: source.title,
          iconTooltipText: this.intl.t(
            'ai-agent.playground.inspect-sources.tooltips.custom-action',
          ),
          link: {
            route: 'apps.app.settings.app-settings.custom-actions.custom-action',
            models: [this.session.workspace, source.entityId],
          },
        })) || [];

    settings.push(...workflowConnectorActions);

    if (this.args.realtimeTranslationApplied) {
      settings.push({
        id: 'real-time-translation',
        icon: this.realTimeTranslationIcon,
        title: this.intl.t('ai-agent.playground.real-time-translation'),
        iconTooltipText: this.intl.t(
          'ai-agent.playground.inspect-sources.tooltips.multilingual-support',
        ),
        link: {
          route: 'apps.app.settings.ai-automation.fin-ai-agent',
          query: { section: 'multilingual-settings' },
        },
      });
    }
    return settings;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Playground::FinAnswer::Inspect::InspectSections': typeof InspectSections;
  }
}
